'use client';

import { ComponentProps, useMemo } from 'react';

import { format, fromUnixTime, parse } from 'date-fns';
import { Configure, ConfigureProps, InstantSearch } from 'react-instantsearch';
import TypesenseInstantSearchAdapter from 'typesense-instantsearch-adapter';

const url = process.env.NEXT_PUBLIC_TYPESENSE_URL;
const orgId = process.env.NEXT_PUBLIC_API_KEY;
const env = process.env.NEXT_PUBLIC_TYPESENSE_ENV || 'production';

// Type for what can go inside an index's UI state
type IndexUiState = NonNullable<ComponentProps<typeof InstantSearch>['initialUiState']>[string];

/**
 * Converts a Unix timestamp to a YYYY-MM-DD date string
 * @param timestamp Unix timestamp in seconds
 * @returns ISO date string (YYYY-MM-DD)
 */
function timestampToDateString(timestamp: string | number): string {
  if (!timestamp) return '';
  return format(fromUnixTime(Number(timestamp)), 'yyyy-MM-dd');
}

/**
 * Converts a YYYY-MM-DD date string to a Unix timestamp
 * @param dateString ISO date string (YYYY-MM-DD)
 * @returns Unix timestamp in seconds
 */
function dateStringToTimestamp(dateString: string): number | '' {
  if (!dateString) return '';
  const date = parse(dateString, 'yyyy-MM-dd', new Date());
  return Math.floor(date.getTime() / 1000);
}

/**
 * Process date ranges from Typesense format to URL-friendly format
 * @param dateRange Typesense date range (start:end format with timestamps)
 * @returns Object with from/to properties as YYYY-MM-DD strings
 */
function processDateRangeForUrl(dateRange?: string): { from?: string; to?: string } {
  if (!dateRange) return {};

  const [start, end] = dateRange.split(':');
  const result: { from?: string; to?: string } = {};

  if (start) {
    result.from = timestampToDateString(start);
  }

  if (end) {
    result.to = timestampToDateString(end);
  }

  return result;
}

/**
 * Convert URL-friendly date parameters back to Typesense format
 * @param from Start date as YYYY-MM-DD
 * @param to End date as YYYY-MM-DD
 * @returns Formatted date range string for Typesense (start:end)
 */
function processUrlDatesToRange(from?: string, to?: string): { dates?: string } {
  if (!from && !to) return {};

  const fromTimestamp = from ? dateStringToTimestamp(from) : '';
  const toTimestamp = to ? dateStringToTimestamp(to) : '';

  return {
    dates: `${fromTimestamp}${toTimestamp ? ':' + toTimestamp : ':'}`,
  };
}

export function SearchContext({
  children,
  contentForm,
  configure,
  sortBy,
  initialUiState,
  routing = true,
}: {
  children: React.ReactNode;
  contentForm: string;
  configure: ConfigureProps;
  sortBy?: string;
  initialUiState?: IndexUiState;
  routing?: boolean;
}) {
  if (!url) throw new Error('TYPESENSE_URL is not set');
  if (!orgId) throw new Error('NEXT_PUBLIC_API_KEY is not set');

  // Memoize the adapter instance to prevent recreation on every render
  const typesense = useMemo(() => {
    return new TypesenseInstantSearchAdapter({
      server: {
        nodes: [
          {
            host: url.replace('https://', ''),
            port: 443,
            protocol: 'https',
          },
        ],
        apiKey: orgId,
      },
      additionalSearchParameters: {
        preset: 'search_only',
        ...(sortBy && { sort_by: sortBy }),
      },
    });
  }, [sortBy]);

  const indexName = `${orgId}:${env}:${contentForm}`;

  // Memoize the initial UI state to prevent recreation on every render
  const memoizedInitialUiState = useMemo(() => {
    return {
      [indexName]: {
        ...initialUiState,
      },
    };
  }, [indexName, initialUiState]);

  const routingProps = {
    stateMapping: {
      // @ts-expect-error - shut up ts
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      stateToRoute({ [indexName]: { configure, ...indexState } }) {
        // Handle special processing for dates in range
        const processedRange = { ...indexState.range };

        // Convert timestamp ranges to user-friendly dates
        if (indexState.range?.dates) {
          const dateParams = processDateRangeForUrl(indexState.range.dates);
          Object.assign(processedRange, dateParams);
          delete processedRange.dates;
        }

        return {
          ...indexState.refinementList,
          ...processedRange,
          page: indexState.page,
        };
      },
      // @ts-expect-error - shut up ts
      routeToState(routeState) {
        // If no route data exists, return empty object to use initialUiState
        if (Object.keys(routeState).length === 0) {
          return {};
        }

        const { page, from, to, ...rest } = routeState;

        // Convert user-friendly dates back to timestamp ranges
        const range = processUrlDatesToRange(from as string, to as string);

        return {
          [indexName]: {
            refinementList: Object.fromEntries(
              Object.entries(rest).map(([key, value]) => {
                if (!Array.isArray(value)) return [key, value as string];

                return [key, value.map(decodeURIComponent)];
              }),
            ),
            range: {
              ...Object.fromEntries(
                Object.entries(rest).map(([key, value]) => {
                  if (!Array.isArray(value)) return [key, value as string];

                  return [key, value.map(decodeURIComponent)];
                }),
              ),
              ...range,
            },
            page,
          },
        };
      },
    },
  };

  return (
    <InstantSearch
      routing={routing && routingProps}
      searchClient={typesense.searchClient}
      indexName={indexName}
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      initialUiState={memoizedInitialUiState as any}
      future={{ preserveSharedStateOnUnmount: true }}
    >
      <Configure {...configure} />

      {children}
    </InstantSearch>
  );
}
